'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import Axios from '@/lib/axiosConfig';
import { toast } from 'sonner';

export const useVotePollMutation = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async ({
      postId,
      choiceID,
    }: {
      postId: string;
      choiceID: string;
      /** Set when this vote replaces another: the caller shows one toast. */
      replacing?: boolean;
    }) => {
      const url = `/posts/${postId}/polls/vote`;
      const payload = { choiceID };

      return Axios.post(url, payload);
    },
    onSuccess: (_, variables) => {
      // `useGetPostById` caches under ['posts', id]. The feed and group lists are
      // rendered from server components, so they need router.refresh() — query
      // invalidation alone can't reach them.
      queryClient.invalidateQueries({ queryKey: ['posts', variables.postId] });
      router.refresh();
      toast.success(variables.replacing ? 'Vote changed' : 'Voted successfully');
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Something went wrong with your vote'
      );
    },
  });
};

// Removes the caller's vote. Used on its own (tap your choice again) and as
// the first half of changing a vote (remove, then vote for the new option).
export const useRemovePollVoteMutation = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async ({
      postId,
    }: {
      postId: string;
      /** Set when a new vote follows: skip the refresh and toast until then. */
      silent?: boolean;
    }) => {
      return Axios.delete(`/posts/${postId}/polls/vote`);
    },
    onSuccess: (_, variables) => {
      if (variables.silent) return;
      queryClient.invalidateQueries({ queryKey: ['posts', variables.postId] });
      router.refresh();
      toast.success('Vote removed');
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || 'Could not remove your vote'
      );
    },
  });
};

export const useClosePollMutation = () => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: async (postId: string) => {
      return Axios.put(`/posts/${postId}/polls/close`, {});
    },
    onSuccess: (_, postId) => {
      toast.success('Poll closed');
      queryClient.invalidateQueries({ queryKey: ['posts', postId] });
      router.refresh();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to close poll');
    },
  });
};